import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Drawer, FormField, Input, SegmentedTabs, toast } from '@/ui';
import type { MemberType } from '@/auth/types';
import { LEGAL_COPY } from '@/features/legal/legal-copy';
import { provisionOrganizationMember, type ProvisionResult } from './api';
import { invalidateAfterOrganizationMutation } from './invalidation';
import { adminErrorMessage } from './error-message';
import styles from './Admin.module.css';

/**
 * ============ ISLETMEYE UYE EKLEME (/yonetim/isletmeler/:id) ============
 *
 * Isletme yoneticisinin kendi ekibine kullandigi cekmecenin (InviteMemberDrawer) platform
 * kapsamli ikizi. Iki fark:
 *   1. Organizasyon govdeden gelir (yonetici kendi org'una eklerken token'dan geliyordu).
 *   2. YETKI SECICI YOK. Gunluk operasyon yetkilerini dagitmak isletme yoneticisinin isi;
 *      platform yoneticisi HESABI acar. (Musteri personeline gereken `viewAccounts`
 *      varsayilanini sunucu kendisi veriyor.)
 *
 * Yeni kullanicinin `user_type`'ini sunucu ORGANIZASYONUN TIPINDEN turetir — istemci
 * gondermez, gonderemez. Bu, "musteri org'una exchange personeli eklendi" sinifindaki
 * sessiz hatanin kapatildigi yer.
 */

const makeSchema = (t: TFunction) =>
  z.object({
    invitedFullName: z.string().trim().min(1, t('validation.fullNameRequired')),
    invitedEmail: z.string().trim().email(t('validation.emailInvalid')),
  });

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

type ProvisionMemberDrawerProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
};

export function ProvisionMemberDrawer({
  open,
  onClose,
  organizationId,
  organizationName,
}: ProvisionMemberDrawerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [memberType, setMemberType] = useState<MemberType>('staff');
  const [created, setCreated] = useState<ProvisionResult | null>(null);
  const [createdEmail, setCreatedEmail] = useState('');

  const schema = useMemo(() => makeSchema(t), [t]);
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { invitedFullName: '', invitedEmail: '' },
  });

  const create = useMutation({
    mutationFn: provisionOrganizationMember,
    onSuccess: async (result) => {
      setCreated(result);
      setCreatedEmail(getValues('invitedEmail').trim());
      reset();
      await invalidateAfterOrganizationMutation(queryClient);
      if (result.emailDelivered) {
        toast({ message: t('admin.organizations.memberCreated'), tone: 'success' });
      }
    },
    onError: (error) => {
      toast({ message: adminErrorMessage(error, t), tone: 'danger' });
    },
  });

  const onSubmit = handleSubmit((values) => {
    create.mutate({
      organizationId,
      invitedFullName: values.invitedFullName.trim(),
      invitedEmail: values.invitedEmail.trim(),
      memberTypeCode: memberType,
    });
  });

  const close = (): void => {
    // Gecici sifre bir daha alinamaz; panel kapanirken durum temizleniyor.
    setCreated(null);
    setCreatedEmail('');
    reset();
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title={t('admin.organizations.addMemberTitle')}
      footer={
        <>
          <Button tone="ghost" onClick={close}>
            {created ? t('members.done') : t('auth.give')}
          </Button>
          <Button loading={create.isPending} onClick={() => void onSubmit()}>
            {t('members.createAccount')}
          </Button>
        </>
      }
    >
      <form className={styles.composer} onSubmit={(event) => void onSubmit(event)} noValidate>
        <p className={styles.muted}>
          {t('admin.organizations.addMemberHint', { name: organizationName })}
        </p>

        <FormField label={t('org.invite.fullName')} error={errors.invitedFullName} required>
          <Input autoComplete="off" {...register('invitedFullName')} />
        </FormField>

        <FormField label={t('org.invite.email')} error={errors.invitedEmail} required>
          <Input type="email" inputMode="email" autoComplete="off" {...register('invitedEmail')} />
        </FormField>

        <div>
          <p className={styles.sectionTitle}>{t('org.invite.role')}</p>
          <SegmentedTabs<MemberType>
            label={t('org.invite.role')}
            value={memberType}
            onChange={setMemberType}
            items={[
              { value: 'staff', label: t('members.makeStaff') },
              { value: 'admin', label: t('members.makeAdmin') },
            ]}
          />
          <p className={styles.muted}>
            {memberType === 'admin' ? t('org.invite.roleAdminHint') : t('org.invite.roleStaffHint')}
          </p>
        </div>

        {/* Yoneticiye, BASKASININ verisini girdigini hatirlatir (KVKK md.10). */}
        <p className={styles.warningNotice}>{LEGAL_COPY.organization.inviteAdminWarning}</p>

        {created && !created.emailDelivered && created.tempPassword ? (
          <div className={styles.warningNotice}>
            <p>{t('org.invite.createdNoEmail')}</p>
            <p>{createdEmail}</p>
            <code className={styles.tempPassword}>{created.tempPassword}</code>
            <p>{LEGAL_COPY.organization.inviteNotice}</p>
          </div>
        ) : null}

        {created?.emailDelivered ? (
          <p className={styles.notice}>{t('org.invite.passwordEmailed')}</p>
        ) : null}
      </form>
    </Drawer>
  );
}
